import React from 'react';
import { useNavigate } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import { MktButton } from './marketplaceUi';
import { MARKETPLACE_HOME_PATH } from './marketplaceLinks';

interface Props {
  title?: string;
  message?: string;
  code?: string;
  onRetry?: () => void;
}

/**
 * Branded error/empty screen for the marketplace — replaces raw red error text
 * (#49). Use errorScreenPropsFor() to map an API error message to a friendly
 * title/message (not-found, no-access, generic).
 */
const MarketplaceErrorScreen: React.FC<Props> = ({
  title = 'Something went wrong',
  message = 'We couldn’t load this page. Please try again in a moment.',
  code,
  onRetry,
}) => {
  const navigate = useNavigate();
  return (
    <MarketplaceShell maxWidth="max-w-[520px]">
      <div
        className="flex flex-col items-center gap-4 rounded-xl border border-black/10 bg-white p-10 text-center"
        role="alert"
      >
        {code && <div className="text-[40px] font-medium leading-none text-[#111]">{code}</div>}
        <h1 className="text-[18px] font-medium text-[#111]">{title}</h1>
        <p className="text-[13px] leading-relaxed text-[#6b6b6b]">{message}</p>
        <div className="flex items-center gap-2">
          {onRetry && <MktButton onClick={onRetry}>Try again</MktButton>}
          <MktButton
            variant={onRetry ? 'secondary' : 'primary'}
            onClick={() => navigate(MARKETPLACE_HOME_PATH)}
          >
            Back to the marketplace →
          </MktButton>
        </div>
      </div>
    </MarketplaceShell>
  );
};

export function errorScreenPropsFor(raw: string | null | undefined): Props {
  const m = (raw || '').toLowerCase();
  if (m.includes('not found') || m.includes('404')) {
    return {
      code: '404',
      title: 'Study not found',
      message: 'This study doesn’t exist or may have been removed.',
    };
  }
  if (
    m.includes('not authenticated') ||
    m.includes('unauthor') ||
    m.includes('403') ||
    m.includes('401') ||
    m.includes('access')
  ) {
    return {
      code: '403',
      title: 'You don’t have access to this study',
      message:
        'This study belongs to a different account. Sign in with the account that created it to view the results.',
    };
  }
  // Don't surface the raw technical error string to users (the #49 regression).
  return {
    title: 'Something went wrong',
    message: 'We couldn’t load this study. Please try again in a moment.',
  };
}

export default MarketplaceErrorScreen;
